import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  BarChart3,
  Database,
  Film,
  Flag,
  Gift,
  LayoutDashboard,
  Layers,
  Megaphone,
  Menu,
  Share2,
  ShoppingBag,
  Users,
  Video,
  Wallet,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { adminNavGroups, type AdminTab } from "@/components/admin/adminConstants";

const tabIcons: Record<AdminTab, typeof LayoutDashboard> = {
  dashboard: LayoutDashboard,
  platform: Layers,
  finance: Wallet,
  economy: ShoppingBag,
  rewards: Gift,
  data: Database,
  users: Users,
  posts: Film,
  drifts: Video,
  parallels: Share2,
  reports: Flag,
  promotions: Megaphone,
  ads: BarChart3,
  activity: Activity,
};

type Props = {
  tab: AdminTab;
  onTabChange: (tab: AdminTab) => void;
  reportBadge?: number;
  onSignOut: () => void;
  children: React.ReactNode;
};

function SidebarNav({
  tab,
  onTabChange,
  reportBadge,
  onNavigate,
}: {
  tab: AdminTab;
  onTabChange: (t: AdminTab) => void;
  reportBadge?: number;
  onNavigate?: () => void;
}) {
  return (
    <nav className="flex flex-col gap-6 p-4">
      <div className="px-2">
        <div className="text-lg font-bold tracking-tight">Daywia</div>
        <div className="text-xs text-ink-muted">Admin panel</div>
      </div>
      {adminNavGroups.map((group) => (
        <div key={group.title}>
          <div className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-ink-muted/80">
            {group.title}
          </div>
          <ul className="space-y-0.5">
            {group.items.map((item) => {
              const Icon = tabIcons[item.id];
              const active = tab === item.id;
              const badge =
                item.badgeKey === "reports" && reportBadge ? reportBadge : undefined;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onTabChange(item.id);
                      onNavigate?.();
                    }}
                    className={[
                      "flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium transition",
                      active
                        ? "bg-[#111827] text-white shadow-sm"
                        : "text-ink-muted hover:bg-black/[0.04] hover:text-ink",
                    ].join(" ")}
                  >
                    <Icon className="h-4 w-4 shrink-0 opacity-90" strokeWidth={2} />
                    <span className="truncate">{item.label}</span>
                    {badge ? (
                      <span
                        className={[
                          "ml-auto rounded-full px-1.5 py-0.5 text-[10px] font-bold",
                          active ? "bg-white/20 text-white" : "bg-red-500 text-white",
                        ].join(" ")}
                      >
                        {badge}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

export function AdminShell({ tab, onTabChange, reportBadge, onSignOut, children }: Props) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="admin-panel min-h-dvh bg-[#f3f4f6] text-ink">
      {/* Mobile overlay */}
      {mobileOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          aria-label="Close menu"
          onClick={() => setMobileOpen(false)}
        />
      ) : null}

      {/* Sidebar — desktop fixed, mobile drawer */}
      <aside
        className={[
          "fixed inset-y-0 left-0 z-50 w-[260px] border-r border-black/[0.06] bg-white transition-transform lg:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        ].join(" ")}
      >
        <div className="flex items-center justify-between border-b border-black/[0.06] p-3 lg:hidden">
          <span className="font-semibold">Menu</span>
          <button type="button" onClick={() => setMobileOpen(false)} aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <SidebarNav
          tab={tab}
          onTabChange={onTabChange}
          reportBadge={reportBadge}
          onNavigate={() => setMobileOpen(false)}
        />
      </aside>

      <div className="lg:pl-[260px]">
        <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-black/[0.06] bg-white/95 px-4 py-3 backdrop-blur">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="rounded-lg p-2 hover:bg-black/[0.04] lg:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-base font-semibold capitalize">
                {adminNavGroups.flatMap((g) => g.items).find((i) => i.id === tab)?.label ?? tab}
              </h1>
              <p className="hidden text-xs text-ink-muted sm:block">
                Platform analytics & moderation
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="hidden text-sm font-medium text-primary hover:underline sm:inline"
            >
              Open app
            </Link>
            <Button variant="outline" onClick={onSignOut}>
              Sign out
            </Button>
          </div>
        </header>

        <main className="p-4 md:p-6">{children}</main>
      </div>
    </div>
  );
}

export function AdminCard({
  title,
  action,
  children,
  className = "",
}: {
  title?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={["rounded-2xl border border-black/[0.06] bg-white p-4 shadow-sm md:p-5", className].join(
        " ",
      )}
    >
      {title ? (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          {action}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function MetricCard({
  label,
  value,
  sub,
  trend,
}: {
  label: string;
  value: string;
  sub?: string;
  trend?: { value: string; positive?: boolean };
}) {
  return (
    <div className="rounded-2xl border border-black/[0.06] bg-white p-4 shadow-sm">
      <div className="text-xs font-medium text-ink-muted">{label}</div>
      <div className="mt-1 text-2xl font-bold tabular-nums tracking-tight">{value}</div>
      {sub ? <div className="mt-1 text-xs text-ink-muted">{sub}</div> : null}
      {trend ? (
        <div
          className={[
            "mt-2 inline-flex rounded-md px-1.5 py-0.5 text-xs font-medium",
            trend.positive ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600",
          ].join(" ")}
        >
          {trend.value}
        </div>
      ) : null}
    </div>
  );
}
